import { useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { DownloadIcon, PlusIcon, UploadIcon } from 'lucide-react';

import {
  ApiError, api, downloadFile, type LifecycleImpact, type QuestionRow,
} from '@/lib/api';
import { useContent, useLabel, useT, useTn } from '@/lib/i18n';
import { notify } from '@/lib/notify';
import { PageHeader } from '@/components/page-header';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Dialog, DialogTrigger } from '@/components/ui/dialog';
import { ITEM_TYPES, ITEM_TYPE_IDS, ItemEditor } from '@/features/questions/editor';
import { ImportDialog } from '@/features/questions/import-dialog';
import { BulkBar } from '@/features/questions/bulk-bar';
import { SavedFilters } from '@/components/saved-filters';
import { useUrlState } from '@/lib/url-state';
import { useSearchShortcut } from '@/lib/shortcuts';
import { useModuleName } from '@/hooks/use-module-name';
import { useCannotBecause } from '@/lib/permissions';

/**
 * The bilingual item bank (build/09 §3).
 *
 * SERVER-FILTERED AND SERVER-PAGINATED, never 612 items in the browser. Every
 * filter below is a query parameter; changing one refetches. Sorting or
 * filtering the fetched page client-side would silently operate on 25 rows and
 * look like it operated on the bank.
 *
 * ARABIC IS NOT A SECONDARY FIELD. Both prompts sit side by side in the editor,
 * the Arabic input carries `dir="rtl"`, and the table shows both — a certified
 * item is required by the contract to have both, so an editor that treats
 * Arabic as optional is an editor that produces items which cannot be certified.
 *
 * The form itself lives in `features/questions/editor.tsx` and covers all seven
 * item types (`plan-beta` B1). This file is the list, the filters and the
 * lifecycle actions.
 */

const STATUS_TONE: Record<string, 'default' | 'secondary' | 'outline' | 'destructive'> = {
  certified: 'default',
  review: 'secondary',
  draft: 'outline',
  retired: 'destructive',
};

const ALL = '__all__';

export function Questions() {
  const queryClient = useQueryClient();
  const t = useT();
  const tn = useTn();
  const label = useLabel();
  const content = useContent();
  /**
   * THE FILTERS LIVE IN THE URL (`plan-beta` E, and B5's carry-forward).
   *
   * So this view is a link. "Certified behavioural sliders" was a set of clicks
   * to describe and is now something you paste into a message — which is also
   * what makes the saved-filter control below possible without it knowing a
   * single thing about what this list filters on.
   */
  const [filters, setFilters] = useUrlState({
    module: ALL, type: ALL, status: ALL, q: '', page: '1',
  });
  const { module: moduleId, type: itemType, status, q: search } = filters;
  const page = Number(filters.page) || 1;

  const searchBox = useRef<HTMLInputElement>(null);
  useSearchShortcut(searchBox);

  const [editorOpen, setEditorOpen] = useState(false);
  /**
   * The selection lives here, not in `<DataTable>` — it has to survive paging,
   * refetching and the table re-rendering, and it is this screen that acts on it.
   */
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  /** The item being corrected, or undefined when the dialog is authoring a new one. */
  const [editing, setEditing] = useState<QuestionRow | undefined>(undefined);

  const modules = useQuery({ queryKey: ['modules'], queryFn: api.modules });
  const moduleName = useModuleName();
  /**
   * R3. Every write on this screen — create, edit, the five lifecycle
   * transitions, import, and the bulk bar — is one capability: `items:author`.
   *
   * **Export is deliberately not gated.** `GET /questions/export` is a read, and
   * an analyst who cannot author is exactly the person most likely to want the
   * bank in a spreadsheet.
   */
  const cannotAuthor = useCannotBecause('items:author');


  const params = {
    page,
    pageSize: 25,
    ...(moduleId === ALL ? {} : { moduleId }),
    ...(itemType === ALL ? {} : { itemType }),
    ...(status === ALL ? {} : { status }),
    ...(search.trim() ? { q: search.trim() } : {}),
  };

  const bank = useQuery({ queryKey: ['questions', params], queryFn: () => api.questions(params) });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['questions'] });
    void queryClient.invalidateQueries({ queryKey: ['modules'] });
    // Every transition below changes the certified pool, which is what coverage
    // measures — so the matrix screen must not keep showing the old colour.
    void queryClient.invalidateQueries({ queryKey: ['coverage'] });
  };

  const openEditor = (item?: QuestionRow) => {
    setEditing(item);
    setEditorOpen(true);
  };

  /**
   * Export what is on screen — the same filters, not a second selection.
   *
   * `page` and `pageSize` are deliberately dropped: an operator filtering to
   * "behavioural, certified" and pressing Export means that set, not the
   * twenty-five rows of it they happen to be looking at.
   */
  const exportCsv = useMutation({
    mutationFn: () => {
      const { page: _p, pageSize: _s, ...filters } = params;
      return api.exportQuestions(filters);
    },
    onSuccess: (csv) => {
      downloadFile(`items-${new Date().toISOString().slice(0, 10)}.csv`, csv, 'text/csv;charset=utf-8');
      notify.success(t('questions.export.ready'), t('questions.export.hint'));
    },
    onError: (err) =>
      notify.error(
        t('questions.export.failed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  /**
   * One key per failed transition rather than "Could not {verb}".
   *
   * The interpolated version needs the verb in a form the sentence can take, and
   * Arabic inflects it — "تعذّر الاعتماد" is not "تعذّر" plus a word. This is the
   * commonest way a translated UI ends up reading like a machine wrote it.
   */
  const onTransitionError =
    (key: 'review' | 'certify' | 'uncertify' | 'retire' | 'unretire') => (err: unknown) =>
      notify.error(
        t(`questions.failed.${key}`),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      );

  /**
   * The two transitions that shrink the pool report what they did to it.
   *
   * A newly-amber cell is the reason `plan-beta` B2 allows un-certify at all:
   * forbidding it makes a typo in a certified item permanent, so the deal is
   * that it is allowed and the consequence is stated. Reporting it as a plain
   * success toast would be the same thing as hiding it.
   */
  const announceImpact =
    (verb: 'uncertified' | 'retired') => ({ item, impact }: LifecycleImpact) => {
      const amber = impact.newlyWarning;
      const headline = t(`questions.impact.${verb}`, { item: item.itemId });

      if (amber.length > 0) {
        notify.error(
          tn(`questions.impact.${verb}.amber`, amber.length, { item: item.itemId }),
          t('questions.impact.amber.detail', {
            cells: amber.map((c) => `${c.personaId} × ${c.moduleId}`).join(', '),
          }),
        );
      } else if (impact.pinnedBy.length > 0) {
        notify.info(
          headline,
          tn('questions.impact.pinned', impact.pinnedBy.length, {
            cells: impact.pinnedBy.join(', '),
          }),
        );
      } else {
        notify.success(headline, t('questions.impact.none'));
      }
      invalidate();
    };

  /**
   * OPTIMISTIC LIFECYCLE TRANSITIONS (`plan-beta` E).
   *
   * *"Nothing is optimistic, so every certify feels slow."* Certifying is one
   * document update and it never conflicts with itself, so the badge can move
   * the moment it is clicked and be corrected if the server disagrees.
   *
   * **Rolled back on error, always.** The snapshot is taken before the patch and
   * restored in `onError`; an optimistic update that cannot undo itself is just
   * a lie with better latency — and this list can genuinely be refused
   * (`INVALID_TRANSITION` when someone else moved the item first), which is
   * exactly the case the rollback exists for.
   *
   * Applied to the STATUS only. The coverage impact of un-certify and retire is
   * computed server-side and cannot be guessed here, so those still wait for
   * their answer before reporting it.
   */
  const optimisticStatus = (to: string) => ({
    onMutate: async (itemId: string) => {
      await queryClient.cancelQueries({ queryKey: ['questions'] });
      const previous = queryClient.getQueryData(['questions', params]);

      queryClient.setQueryData(
        ['questions', params],
        (old: { items: QuestionRow[] } | undefined) =>
          old && {
            ...old,
            items: old.items.map((row) => (row.itemId === itemId ? { ...row, status: to } : row)),
          },
      );
      return { previous };
    },
    onError: (_err: unknown, _itemId: string, ctx: { previous: unknown } | undefined) => {
      // Put the row back exactly as it was, rather than refetching — a refetch
      // would also discard whatever else the operator has changed meanwhile.
      if (ctx) queryClient.setQueryData(['questions', params], ctx.previous);
    },
  });

  const review = useMutation({
    ...optimisticStatus('review'),
    mutationFn: (itemId: string) => api.reviewQuestion(itemId),
    onSuccess: (item) => {
      notify.success(t('questions.sentForReview'), t('questions.sentForReview.detail', { item: item.itemId }));
      invalidate();
    },
    onError: onTransitionError('review'),
  });

  const certify = useMutation({
    ...optimisticStatus('certified'),
    mutationFn: (itemId: string) => api.certifyQuestion(itemId),
    onSuccess: (item) => {
      notify.success(t('questions.certified'), t('questions.certified.detail', { item: item.itemId }));
      invalidate();
    },
    onError: onTransitionError('certify'),
  });

  const uncertify = useMutation({
    mutationFn: (itemId: string) => api.uncertifyQuestion(itemId),
    onSuccess: announceImpact('uncertified'),
    onError: onTransitionError('uncertify'),
  });

  const retire = useMutation({
    mutationFn: (itemId: string) => api.retireQuestion(itemId),
    onSuccess: announceImpact('retired'),
    onError: onTransitionError('retire'),
  });

  const unretire = useMutation({
    ...optimisticStatus('draft'),
    mutationFn: (itemId: string) => api.unretireQuestion(itemId),
    onSuccess: (item) => {
      notify.success(t('questions.unretired'), t('questions.unretired.detail', { item: item.itemId }));
      invalidate();
    },
    onError: onTransitionError('unretire'),
  });

  const busy = review.isPending || certify.isPending || uncertify.isPending
    || retire.isPending || unretire.isPending;

  const columns = useMemo<ColumnDef<QuestionRow, unknown>[]>(
    () => [
      {
        header: t('questions.column.prompt'),
        accessorKey: 'prompt',
        // ONE LANGUAGE — the console's (2026-08-02). This bank listed every
        // prompt twice, English above Arabic, which halved the rows on screen
        // and made scanning it in either language harder than reading it in
        // neither. The pair belongs to the EDITOR, where an author is comparing
        // two translations; a list is for finding an item.
        //
        // `content()` still falls back to the other language for a
        // half-translated item, so a row is never blank — it just is not two
        // rows tall.
        cell: ({ row }) => (
          <div className="min-w-64">
            <p className="font-medium">{content(row.original.prompt)}</p>
          </div>
        ),
      },
      {
        header: t('questions.column.module'),
        accessorKey: 'moduleId',
        cell: ({ row }) => moduleName(row.original.moduleId),
      },
      {
        header: t('questions.column.type'),
        accessorKey: 'itemType',
        cell: ({ row }) => <span className="font-mono text-xs" dir="ltr">{row.original.itemType}</span>,
      },
      {
        header: t('questions.column.status'),
        accessorKey: 'status',
        cell: ({ row }) => (
          <Badge variant={STATUS_TONE[row.original.status] ?? 'outline'}>
            {label('status', row.original.status)}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const item = row.original;
          /**
           * The actions offered ARE the transitions the server will accept —
           * `LIFECYCLE` in routes/authoring.ts, mirrored. Hiding a control is
           * never the enforcement (a 422 is), but offering one that always
           * fails is its own kind of lie.
           */
          return (
            <div className="flex justify-end gap-1">
              <Button size="sm" variant="ghost" onClick={() => openEditor(item)}>
                {t('common.edit')}
              </Button>

              {item.status === 'draft' && (
                <Button size="sm" variant="ghost" disabled={busy || cannotAuthor !== null}
                  title={cannotAuthor ?? undefined}
                  onClick={() => review.mutate(item.itemId)}>
                  {t('questions.action.review')}
                </Button>
              )}
              {(item.status === 'draft' || item.status === 'review') && (
                <Button size="sm" variant="outline" disabled={busy || cannotAuthor !== null}
                  title={cannotAuthor ?? undefined}
                  onClick={() => certify.mutate(item.itemId)}>
                  {t('questions.action.certify')}
                </Button>
              )}
              {item.status === 'certified' && (
                <Button size="sm" variant="outline" disabled={busy || cannotAuthor !== null}
                  title={cannotAuthor ?? undefined}
                  onClick={() => uncertify.mutate(item.itemId)}>
                  {t('questions.action.uncertify')}
                </Button>
              )}
              {item.status !== 'retired' && (
                <Button size="sm" variant="ghost" disabled={busy || cannotAuthor !== null}
                  title={cannotAuthor ?? undefined}
                  onClick={() => retire.mutate(item.itemId)}>
                  {t('questions.action.retire')}
                </Button>
              )}
              {item.status === 'retired' && (
                <Button size="sm" variant="ghost" disabled={busy || cannotAuthor !== null}
                  title={cannotAuthor ?? undefined}
                  onClick={() => unretire.mutate(item.itemId)}>
                  {t('questions.action.unretire')}
                </Button>
              )}
            </div>
          );
        },
      },
    ],
    // `t`, `label` and `content` are memoised on the locale, so the headers and
    // badges re-render when the language changes rather than freezing at
    // whatever was active when the table first mounted.
    [busy, review, certify, uncertify, retire, unretire, t, label, content, moduleName],
  );

  const moduleIds = (modules.data?.items ?? []).map((m) => m.moduleId);

  return (
    <div className="mx-auto max-w-6xl">
      <PageHeader
        title={t('nav.questions.all')}
        description={t('questions.description')}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={exportCsv.isPending}
              onClick={() => exportCsv.mutate()}
            >
              <DownloadIcon className="size-4" />
              {exportCsv.isPending ? t('questions.exporting') : t('questions.exportCsv')}
            </Button>

            <Dialog open={importOpen} onOpenChange={setImportOpen}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" disabled={cannotAuthor !== null} title={cannotAuthor ?? undefined}>
                  <UploadIcon className="size-4" />
                  {t('questions.importCsv')}
                </Button>
              </DialogTrigger>
              <ImportDialog
                key={importOpen ? 'open' : 'closed'}
                onImported={() => { setImportOpen(false); invalidate(); }}
              />
            </Dialog>

            <Dialog
              open={editorOpen}
              onOpenChange={(open) => {
                setEditorOpen(open);
                if (!open) setEditing(undefined);
              }}
            >
              <DialogTrigger asChild>
                <Button size="sm" onClick={() => setEditing(undefined)}
                  disabled={cannotAuthor !== null} title={cannotAuthor ?? undefined}>
                  <PlusIcon className="size-4" />
                  {t('questions.newItem')}
                </Button>
              </DialogTrigger>
              {/*
                Keyed on the item so opening a different row rebuilds the form's
                state rather than reusing the previous row's draft — a dialog that
                keeps its state across two items is how one item's options end up
                saved onto another.
              */}
              <ItemEditor
                key={editing?.itemId ?? 'new'}
                moduleIds={moduleIds}
                initial={editing}
                onSaved={() => {
                  setEditorOpen(false);
                  setEditing(undefined);
                  invalidate();
                }}
              />
            </Dialog>
          </div>
        }
      />

      <div className="mb-3 flex justify-end">
        <SavedFilters />
      </div>

      <div className="mb-4 grid gap-3 sm:grid-cols-4">
        {/* No `setPage(1)` at any of these call sites: `useUrlState` resets the
            page whenever a filter changes, because narrowing a search while on
            page 7 otherwise lands on page 7 of two — an empty list that reads as
            "no results" and is really "no such page". */}
        <Select value={moduleId} onValueChange={(v) => setFilters({ module: v })}>
          <SelectTrigger aria-label={t('dashboard.filterByModule')}>
            <SelectValue placeholder={t('dashboard.allModules')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('dashboard.allModules')}</SelectItem>
            {moduleIds.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
          </SelectContent>
        </Select>

        <Select value={itemType} onValueChange={(v) => setFilters({ type: v })}>
          <SelectTrigger aria-label={t('questions.filterByType')}>
            <SelectValue placeholder={t('questions.allTypes')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('questions.allTypes')}</SelectItem>
            {ITEM_TYPE_IDS.map((id) => (
              <SelectItem key={id} value={id}>{t(ITEM_TYPES[id]!.label)}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={status} onValueChange={(v) => setFilters({ status: v })}>
          <SelectTrigger aria-label={t('questions.filterByStatus')}>
            <SelectValue placeholder={t('questions.allStatuses')} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>{t('questions.allStatuses')}</SelectItem>
            <SelectItem value="draft">{t('status.draft')}</SelectItem>
            <SelectItem value="review">{t('status.review')}</SelectItem>
            <SelectItem value="certified">{t('status.certified')}</SelectItem>
            <SelectItem value="retired">{t('status.retired')}</SelectItem>
          </SelectContent>
        </Select>

        <Input
          ref={searchBox}
          value={search}
          onChange={(e) => setFilters({ q: e.target.value })}
          placeholder={t('questions.searchPlaceholder')}
          aria-label={t('questions.searchLabel')}
        />
      </div>

      {selected.size > 0 && (
        <BulkBar
          selected={selected}
          onClear={() => setSelected(new Set())}
          onDone={invalidate}
        />
      )}

      <DataTable
        columns={columns}
        rows={bank.data?.items ?? []}
        selection={{
          selected,
          onChange: setSelected,
          idOf: (row) => row.itemId,
        }}
        isLoading={bank.isLoading}
        error={bank.error}
        onRetry={() => void bank.refetch()}
        emptyTitle={t('questions.empty.title')}
        emptyDescription={t('questions.empty.description')}
        // An empty state that only describes emptiness leaves the reader to work
        // out what to do; these two ARE what to do, and which one is offered
        // depends on whether a filter is what emptied the list.
        emptyAction={
          <div className="flex gap-2">
            {(moduleId !== ALL || itemType !== ALL || status !== ALL || search.trim()) && (
              <Button variant="outline" onClick={() => setFilters({
                module: ALL, type: ALL, status: ALL, q: '', page: '1',
              })}>
                {t('questions.empty.clearFilters')}
              </Button>
            )}
            <Button onClick={() => openEditor()}>{t('questions.newItem')}</Button>
          </div>
        }
        page={bank.data?.page}
        pageSize={bank.data?.pageSize}
        total={bank.data?.total}
        onPageChange={(next) => setFilters({ page: String(next) })}
      />
    </div>
  );
}
